import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import './utils/i18n';

// Pages
import { HomePage } from './pages/HomePage';
import { CreateRoomPage } from './pages/CreateRoomPage';
import { JoinRoomPage } from './pages/JoinRoomPage';
import { RoomPage } from './pages/RoomPage';
import { CategoryVotingPage } from './pages/CategoryVotingPage';
import { ImpostorSelectionPage } from './pages/ImpostorSelectionPage';
import { RoleRevealPage } from './pages/RoleRevealPage';
import { GamePage } from './pages/GamePage';
import { VotingPage } from './pages/VotingPage';
import { ResultsPage } from './pages/ResultsPage';
import { LocalGameSetupPage } from './pages/LocalGameSetupPage';
import { LocalGameCardsPage } from './pages/LocalGameCardsPage';

// Hooks
import { useSocket } from './hooks/useSocket';
import { useApi } from './hooks/useApi';

// Types
import { GameScreen, AppState, Player, GameState, VotingResults } from './types';

// Palabras para el juego local
const localWords = {
  animales: ['perro', 'gato', 'elefante', 'león', 'tigre', 'oso', 'conejo', 'pájaro'],
  comida: ['pizza', 'hamburguesa', 'ensalada', 'pasta', 'arroz', 'pollo', 'pescado', 'helado'],
  objetos: ['mesa', 'silla', 'teléfono', 'libro', 'coche', 'casa', 'reloj', 'bolígrafo'],
  películas: ['avatar', 'titanic', 'star wars', 'harry potter', 'avengers', 'batman', 'superman', 'spiderman'],
  lugares: ['parís', 'londres', 'tokio', 'madrid', 'roma', 'nueva york', 'sydney', 'moscú'],
  profesiones: ['médico', 'profesor', 'cocinero', 'piloto', 'bombero', 'policía', 'ingeniero', 'artista']
};

interface LocalGameState {
  players: string[];
  word: string;
  category: string;
  impostorIndices: number[];
}

interface LocalGameConfig {
  players: string[];
  category: string;
  impostorCount: number;
}

const App: React.FC = () => {
  const { socket, emit, on, off } = useSocket();
  const api = useApi();

  const [appState, setAppState] = useState<AppState>({
    currentScreen: 'home',
    language: 'es',
  });

  const [localGameState, setLocalGameState] = useState<LocalGameState | null>(null);
  const [savedLocalConfig, setSavedLocalConfig] = useState<LocalGameConfig | null>(null);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const handleRoomState = (data: any) => {
      console.log('roomState event received:', data);
      console.log('Players in room:', data.players);
      
      setAppState(prev => {
        // Actualizar el player actual con los datos del room si existe
        let updatedPlayer = prev.player;
        if (prev.player && data.players) {
          const playerInRoom = data.players.find((p: Player) => p.id === prev.player!.id);
          if (playerInRoom) {
            updatedPlayer = {
              ...prev.player,
              isHost: playerInRoom.isHost,
              isConnected: playerInRoom.isConnected,
            };
            console.log('Updated current player with room data:', updatedPlayer);
          }
        }
        
        return {
          ...prev,
          room: data,
          player: updatedPlayer,
        };
      });
    };

    const handlePlayerJoined = (data: any) => {
      setAppState(prev => ({
        ...prev,
        room: prev.room ? {
          ...prev.room,
          players: data.totalPlayers ? 
            prev.room.players.map(p => ({ ...p, isConnected: true })) :
            prev.room.players
        } : undefined,
      }));
    };

    const handlePlayerLeft = (data: any) => {
      setAppState(prev => ({
        ...prev,
        room: prev.room ? {
          ...prev.room,
          players: prev.room.players.map(p => 
            p.id === data.player.id ? { ...p, isConnected: false } : p
          )
        } : undefined,
      }));
    };

    const handleGameStarted = (data: GameState) => {
      console.log('gameStarted event received:', data);
      setAppState(prev => ({
        ...prev,
        currentScreen: 'role-reveal',
        gameState: data,
      }));
      console.log('Changed screen to role-reveal');
    };

    const handleCategoryVotingStarted = () => {
      setAppState(prev => ({
        ...prev,
        currentScreen: 'category-voting',
      }));
    };

    const handleCategorySelected = (data: { category: string }) => {
      console.log('Category selected:', data.category);
      // El roomState se actualizará automáticamente
    };

    const handleImpostorSelectionStarted = () => {
      setAppState(prev => ({
        ...prev,
        currentScreen: 'impostor-selection',
      }));
    };

    const handleCardFlipped = (data: { playerId: string; isRevealed: boolean; flipCount: number }) => {
      setAppState(prev => ({
        ...prev,
        gameState: prev.gameState ? {
          ...prev.gameState,
          cardFlipStates: {
            ...prev.gameState.cardFlipStates,
            [data.playerId]: {
              isRevealed: data.isRevealed,
              flipCount: data.flipCount
            }
          }
        } : prev.gameState,
      }));
    };

    const handleVotingStarted = (data: { players: Player[] }) => {
      setAppState(prev => ({
        ...prev,
        currentScreen: 'voting',
      }));
    };

    const handleVotingResults = (data: VotingResults) => {
      setAppState(prev => ({
        ...prev,
        currentScreen: 'results',
        votingResults: data,
      }));
    };

    const handleError = (data: { message: string }) => {
      console.error('Socket error:', data.message);
      alert(data.message);
    };

    // Register event listeners
    on('roomState', handleRoomState);
    on('playerJoined', handlePlayerJoined);
    on('playerLeft', handlePlayerLeft);
    on('categoryVotingStarted', handleCategoryVotingStarted);
    on('categorySelected', handleCategorySelected);
    on('impostorSelectionStarted', handleImpostorSelectionStarted);
    on('gameStarted', handleGameStarted);
    on('cardFlipped', handleCardFlipped);
    on('votingStarted', handleVotingStarted);
    on('votingResults', handleVotingResults);
    on('error', handleError);

    return () => {
      off('roomState', handleRoomState);
      off('playerJoined', handlePlayerJoined);
      off('playerLeft', handlePlayerLeft);
      off('categoryVotingStarted', handleCategoryVotingStarted);
      off('categorySelected', handleCategorySelected);
      off('impostorSelectionStarted', handleImpostorSelectionStarted);
      off('gameStarted', handleGameStarted);
      off('cardFlipped', handleCardFlipped);
      off('votingStarted', handleVotingStarted);
      off('votingResults', handleVotingResults);
      off('error', handleError);
    };
  }, [socket, on, off]);

  // Navigation handlers
  const navigateTo = (screen: GameScreen) => {
    setAppState(prev => ({ ...prev, currentScreen: screen }));
  };

  const goHome = () => {
    setAppState({
      currentScreen: 'home',
      language: appState.language,
    });
  };

  const goBack = () => {
    switch (appState.currentScreen) {
      case 'create-room':
      case 'join-room':
        navigateTo('home');
        break;
      case 'room':
        navigateTo('home');
        break;
      case 'game':
        navigateTo('room');
        break;
      case 'voting':
        navigateTo('game');
        break;
      case 'results':
        navigateTo('room');
        break;
      default:
        navigateTo('home');
    }
  };

  // Room creation handlers
  const handleCreateRoom = async (playerName: string, isLocal: boolean) => {
    try {
      const response = await api.createRoom(playerName, isLocal);
      if (response.error) {
        alert(response.error);
        return;
      }

      // El backend devuelve el player completo con isHost
      const player: Player = {
        id: response.data!.playerId,
        name: playerName,
        isConnected: true,
        isHost: true,  // El creador siempre es el anfitrión
      };

      console.log('Room created, player:', player);

      // Join socket room
      emit('joinRoom', {
        roomId: response.data!.roomId,
        playerId: response.data!.playerId,
      });

      setAppState(prev => ({
        ...prev,
        currentScreen: 'room' as GameScreen,
        player,
        room: {
          id: response.data!.roomId,
          code: response.data!.roomCode,
          isLocal,
          players: [player],
          gameState: 'waiting' as 'waiting' | 'playing' | 'voting' | 'results',
          language: 'es' as 'es' | 'en',
          categories: ['animales', 'comida', 'objetos'],
        },
      }));
    } catch (error) {
      console.error('Error creating room:', error);
      alert('Error al crear la sala');
    }
  };

  const handleCreateLocalRoom = async (players: string[]) => {
    console.log('handleCreateLocalRoom called with players:', players);
    try {
      // Enviar el primer jugador y el resto como localPlayers
      console.log('Calling api.createRoom...');
      const response = await api.createRoom(players[0], true, players.slice(1));
      console.log('API response:', response);
      
      if (response.error) {
        console.error('API error:', response.error);
        alert(response.error);
        return;
      }

      // Usar los jugadores retornados por el backend
      const roomPlayers: Player[] = response.data!.players.map(p => ({
        id: p.id,
        name: p.name,
        isConnected: p.isConnected,
      }));

      console.log('Room players:', roomPlayers);
      console.log('Response data:', response.data);
      console.log('Room ID from backend:', response.data!.roomId);

      // Join socket room with the first player
      console.log('Joining socket room...');
      emit('joinRoom', {
        roomId: response.data!.roomId,
        playerId: response.data!.playerId,
      });

      console.log('Setting app state...');
      setAppState(prev => {
        const newState = {
          ...prev,
          currentScreen: 'room' as GameScreen,
          player: roomPlayers[0],
          room: {
            id: response.data!.roomId,
            code: 'LOCAL',
            isLocal: true,
            players: roomPlayers,
            gameState: 'waiting' as 'waiting' | 'playing' | 'voting' | 'results',
            language: 'es' as 'es' | 'en',
            categories: ['animales', 'comida', 'objetos'],
          },
        };
        console.log('New app state:', newState);
        return newState;
      });
      console.log('Local room created successfully!');
    } catch (error) {
      console.error('Error creating local room:', error);
      alert('Error al crear la sala local: ' + (error as Error).message);
    }
  };

  const handleJoinRoom = async (roomCode: string, playerName: string) => {
    try {
      const response = await api.joinRoom(roomCode, playerName);
      if (response.error) {
        alert(response.error);
        return;
      }

      const player: Player = {
        id: response.data!.playerId,
        name: playerName,
        isConnected: true,
      };

      // Join socket room
      emit('joinRoom', {
        roomId: response.data!.roomId,
        playerId: response.data!.playerId,
      });

      setAppState(prev => ({
        ...prev,
        currentScreen: 'room',
        player,
      }));
    } catch (error) {
      console.error('Error joining room:', error);
      alert('Error al unirse a la sala');
    }
  };

  const handleStartCategoryVoting = () => {
    console.log('handleStartCategoryVoting called');
    if (!socket) {
      console.error('Socket not connected');
      return;
    }
    emit('startCategoryVoting', undefined);
  };

  const handleVoteCategory = (category: string) => {
    console.log('handleVoteCategory called with:', category);
    if (!socket) {
      console.error('Socket not connected');
      return;
    }
    emit('voteCategory', { category });
  };

  const handleSelectImpostorCount = (count: number) => {
    console.log('handleSelectImpostorCount called with:', count);
    if (!socket) {
      console.error('Socket not connected');
      return;
    }
    emit('selectImpostorCount', { count });
  };

  const handleStartGame = async () => {
    console.log('handleStartGame called');
    console.log('Full appState:', appState);
    
    if (!appState.room) {
      console.error('No room found');
      return;
    }

    console.log('Starting game for room:', appState.room.id);
    console.log('Full room object:', appState.room);

    try {
      const response = await api.startGame(appState.room.id);
      console.log('Start game response:', response);
      
      if (response.error) {
        console.error('Start game error:', response.error);
        alert(response.error);
        return;
      }

      console.log('Game started successfully, waiting for socket event...');
    } catch (error) {
      console.error('Error starting game:', error);
      alert('Error al iniciar el juego');
    }
  };

  const handleFlipCard = (playerId: string, reveal: boolean) => {
    console.log('handleFlipCard called', { playerId, reveal });
    if (!socket) {
      console.error('Socket not connected');
      return;
    }
    emit('flipCard', { playerId, reveal });
  };

  const handleContinueToVoting = () => {
    console.log('handleContinueToVoting called');
    if (!appState.room) {
      console.error('No room found');
      return;
    }
    emit('startVoting', { roomId: appState.room.id });
  };

  const handleStartVoting = () => {
    if (!appState.room) return;
    emit('startVoting', { roomId: appState.room.id });
  };

  const handleVote = (targetId: string) => {
    emit('vote', { targetId });
  };

  // Local Game Handlers
  const handleStartLocalGame = (config: LocalGameConfig) => {
    // Guardar la configuración para poder volver a usarla
    setSavedLocalConfig(config);

    // Seleccionar palabra aleatoria de la categoría
    const categoryWords = localWords[config.category as keyof typeof localWords] || localWords.animales;
    const randomWord = categoryWords[Math.floor(Math.random() * categoryWords.length)];

    // Seleccionar impostores aleatoriamente
    const playerIndices = config.players.map((_, i) => i);
    const shuffled = [...playerIndices].sort(() => 0.5 - Math.random());
    const impostorIndices = shuffled.slice(0, config.impostorCount);

    setLocalGameState({
      players: config.players,
      word: randomWord,
      category: config.category,
      impostorIndices,
    });

    setAppState(prev => ({
      ...prev,
      currentScreen: 'local-game-cards' as GameScreen,
    }));
  };

  const handleBackFromLocalGame = () => {
    setLocalGameState(null);
    navigateTo('local-game-setup');
  };

  const handlePlayAgain = () => {
    if (!appState.room) return;
    navigateTo('room');
  };

  // Render current screen
  const renderCurrentScreen = () => {
    switch (appState.currentScreen) {
      case 'home':
        return (
          <HomePage
            onCreateRoom={() => navigateTo('create-room')}
            onJoinRoom={() => navigateTo('join-room')}
            onLocalMode={() => navigateTo('local-game-setup')}
          />
        );

      case 'create-room':
        return (
          <CreateRoomPage
            onBack={goBack}
            onCreateRoom={handleCreateRoom}
            onCreateLocalRoom={handleCreateLocalRoom}
          />
        );

      case 'join-room':
        return (
          <JoinRoomPage
            onBack={goBack}
            onJoinRoom={handleJoinRoom}
          />
        );

      case 'room':
        console.log('Rendering room screen, appState:', appState);
        if (!appState.room || !appState.player) {
          console.error('Missing room or player data!', { room: appState.room, player: appState.player });
          return <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
            <p>Error: No se pudo cargar la sala. Por favor, intenta de nuevo.</p>
          </div>;
        }
        return (
          <RoomPage
            room={appState.room}
            player={appState.player}
            onBack={goBack}
            onStartCategoryVoting={handleStartCategoryVoting}
          />
        );

      case 'category-voting':
        if (!appState.room || !appState.player) return null;
        return (
          <CategoryVotingPage
            players={appState.room.players}
            currentPlayer={appState.player}
            categoryVotes={appState.room.categoryVotes || {}}
            onBack={goBack}
            onVoteCategory={handleVoteCategory}
          />
        );

      case 'impostor-selection':
        if (!appState.room || !appState.player) return null;
        return (
          <ImpostorSelectionPage
            players={appState.room.players}
            currentPlayer={appState.player}
            selectedCategory={appState.room.selectedCategory || 'animales'}
            impostorCount={appState.room.impostorCount}
            onBack={goBack}
            onSelectImpostorCount={handleSelectImpostorCount}
            onStartGame={handleStartGame}
          />
        );

      case 'role-reveal':
        if (!appState.gameState || !appState.player || !appState.room) return null;
        return (
          <RoleRevealPage
            gameState={appState.gameState}
            currentPlayer={appState.player}
            players={appState.room.players}
            onBack={goBack}
            onFlipCard={handleFlipCard}
            onContinueToVoting={handleContinueToVoting}
          />
        );

      case 'game':
        if (!appState.gameState || !appState.player) return null;
        return (
          <GamePage
            gameState={appState.gameState}
            player={appState.player}
            onBack={goBack}
            onStartVoting={handleStartVoting}
          />
        );

      case 'voting':
        if (!appState.room || !appState.player) return null;
        return (
          <VotingPage
            players={appState.room.players}
            currentPlayer={appState.player}
            onBack={goBack}
            onVote={handleVote}
          />
        );

      case 'results':
        if (!appState.votingResults || !appState.room || !appState.gameState) return null;
        return (
          <ResultsPage
            results={appState.votingResults}
            players={appState.room.players}
            word={appState.gameState.word}
            category={appState.gameState.category}
            onBack={goBack}
            onPlayAgain={handlePlayAgain}
            onGoHome={goHome}
          />
        );

      case 'local-game-setup':
        return (
          <LocalGameSetupPage
            onBack={goHome}
            onStartGame={handleStartLocalGame}
            savedConfig={savedLocalConfig}
          />
        );

      case 'local-game-cards':
        if (!localGameState) return null;
        return (
          <LocalGameCardsPage
            players={localGameState.players}
            word={localGameState.word}
            category={localGameState.category}
            impostorIndices={localGameState.impostorIndices}
            onBack={handleBackFromLocalGame}
          />
        );

      default:
        return <HomePage onCreateRoom={() => {}} onJoinRoom={() => {}} onLocalMode={() => {}} />;
    }
  };

  return (
    <div className="App">
      {renderCurrentScreen()}
    </div>
  );
};

export default App;
